import {useState} from 'react';
import groupBy from 'lodash/groupBy';
import {DataGrid} from '@/components/app/data-grid';
import {InputGroup} from '@/components/app/input-group';
import {InputAdornment} from '@/components/app/input-group';
import {Input} from '@/components/ui/input';
import {SegmentedControl} from '@/components/app/inputs';
import {LoadingSkeleton} from '@/components/app/feedback';
import {ActivityTimeline} from '@/components/app/navigation';
import {cn} from '@/lib/utils';
import moment from 'moment';
import {Icon, IconProp} from '../../../app/components/Icon';
import {useGetBiotwinConditionsHistoryQuery} from '../../../app/services/biotwinApi';
import {isDemographic} from '../helpers/utils';
import {BiotwinHistoryItem, BiotwinHistoryType} from '../types';
export interface BiotwinHistoryProps {
  userId: number;
}
export const BiotwinHistory = ({userId}: BiotwinHistoryProps) => {
  const {data: biotwinConditionsHistory = [], isFetching} = useGetBiotwinConditionsHistoryQuery(
    {
      userId,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [globalFilter, setGlobalFilter] = useState('');
  const [viewType, setViewType] = useState<'timeline' | 'table'>('timeline');
  const createdAt = createdAtFactory();
  const tableValue = tableValueFactory();
  const timelineValue = timelineValueFactory();
  return (
    <div>
      <header className={'flex items-center gap-4 mb-4'}>
        <InputGroup position={'left'}>
          <Icon icon="search" />
          <Input onChange={(e) => setGlobalFilter(e.target.value)} placeholder={'Search'} value={globalFilter} />
        </InputGroup>

        <SegmentedControl
          allowEmpty={false}
          className={'ml-auto rounded-full bg-gray-200 w-[18rem]'}
          itemTemplate={(option) => (
            <div className={'flex items-center gap-2'}>
              <Icon icon={option.icon} /> {option.label}
            </div>
          )}
          onValueChange={(e) => {
            if (e) setOrder(e);
          }}
          optionLabel={'label'}
          optionValue={'value'}
          options={[
            {
              icon: 'sort-amount-down-alt',
              label: 'Newest First',
              value: 'desc',
            },
            {
              icon: 'sort-amount-down',
              label: 'Oldest First',
              value: 'asc',
            },
          ]}
          value={order}
        />
        <SegmentedControl
          allowEmpty={false}
          className={'rounded-full bg-gray-200 w-[18rem]'}
          itemTemplate={(option) => (
            <div className={'flex items-center gap-2'}>
              <Icon icon={option.icon} /> {option.label}
            </div>
          )}
          onValueChange={(e) => {
            if (e) setViewType(e);
          }}
          optionLabel={'label'}
          optionValue={'value'}
          options={[
            {
              icon: 'arrows-v',
              label: 'Timeline',
              value: 'timeline',
            },
            {
              icon: 'list',
              label: 'List',
              value: 'table',
            },
          ]}
          value={viewType}
        />
      </header>
      {isFetching && viewType === 'timeline' && (
        <ActivityTimeline
          renderContent={() => {
            return (
              <div className={'flex flex-col gap-4 py-2 my-4'}>
                <LoadingSkeleton height={'2rem'} width={'100%'} />
                <LoadingSkeleton height={'2rem'} width={'100%'} />
                <LoadingSkeleton height={'2rem'} width={'100%'} />
              </div>
            );
          }}
          renderDate={() => {
            return (
              <div className={'flex justify-end'}>
                <LoadingSkeleton height={'1.5rem'} width={'8rem'} />
              </div>
            );
          }}
          items={Array.from({
            length: 3,
          })}
        />
      )}
      {!isFetching && viewType === 'timeline' && (
        <div className={'flex flex-col mt-8'}>
          <ActivityTimeline
            renderContent={([, items]) => {
              const filteredItems = items.filter(
                (item: BiotwinHistoryItem) => !isBasic(item) && !isDemographic(item.conditionName),
              );
              if (filteredItems.length === 0) {
                return (
                  <div className={'bg-white shadow-md border rounded-xl border-zinc-300 px-4 py-2 my-4 text-left'}>
                    <div className={'text-center text-muted-foreground'}>No feature is changed in biotwin.</div>
                  </div>
                );
              }
              return (
                <div className={'bg-white shadow-md border rounded-xl border-zinc-300 px-4 py-2 my-4 text-left'}>
                  <ul className={'list-none m-0 p-0'}>
                    {filteredItems.map((item: BiotwinHistoryItem, index: number) => (
                      <li
                        className={cn({
                          'border-t border-zinc-300': index !== 0,
                        })}
                        key={index}
                      >
                        <div className={'flex items-center gap-4 py-2'}>
                          <Icon className={`text-sm text-${getTypeColor(item)}`} icon={getTypeIcon(item)} />
                          <div className={'flex items-center gap-2'}>
                            {item.conditionName} {`${item.conditionValue ?? ''} ${getConditionUnitName(item)}`}
                            {item.conditionNote && (
                              <span className={'text-sm text-muted-foreground'}>{item.conditionNote}</span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }}
            renderDate={([date]) => {
              return (
                <>
                  {new Date(date).toLocaleDateString('en', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                  {date === createdAt && (
                    <div className={'absolute right-0 mt-2 mr-4 text-blue-600'}>
                      <Icon icon={'star-fill'} /> <span>Created</span>
                    </div>
                  )}
                </>
              );
            }}
            items={timelineValue}
          />
        </div>
      )}
      {isFetching && viewType === 'table' && (
        <div className={'flex flex-col gap-4 py-4'}>
          {Array.from({
            length: 5,
          }).map((_, index) => (
            <div className={'flex gap-4'} key={index}>
              <LoadingSkeleton className={'flex-[3]'} height={'2rem'} width={'1%'} />
              <LoadingSkeleton className={'flex-1'} height={'2rem'} width={'1%'} />
              <LoadingSkeleton className={'flex-1'} height={'2rem'} width={'1%'} />
              <LoadingSkeleton className={'flex-1'} height={'2rem'} width={'1%'} />
            </div>
          ))}
        </div>
      )}
      {!isFetching && viewType === 'table' && (
        <DataGrid
          rows={tableValue}
          columns={[
            {
              key: 'conditionName',
              header: 'Feature',
            },
            {
              key: 'conditionNote',
              header: 'Note',
            },
            {
              render: (row) => new Date(row.conditionDate).toLocaleDateString(),
              className: 'whitespace-nowrap',
              key: 'conditionDate',
              header: 'Date',
            },
            {
              render: (row) => `${row.conditionValue ?? ''} ${getConditionUnitName(row)}`.trim(),
              className: 'whitespace-nowrap',
              key: 'conditionValue',
              header: 'Value',
            },
            {
              render: (row) =>
                [
                  getTypeText(row),
                  row.isPerformedBySystem ? 'by system' : `by ${row.createdByName ?? row.createdByUserName ?? 'user'}`,
                ]
                  .join(' ')
                  .trim(),
              className: 'whitespace-nowrap',
              key: 'type',
              header: 'Action',
            },
            {
              render: (row) => new Date(row.date).toLocaleString(),
              className: 'whitespace-nowrap',
              key: 'date',
              header: 'Action Date',
            },
          ]}
        />
      )}
    </div>
  );
  function getConditionUnitName(item: BiotwinHistoryItem) {
    return item.conditionUnitName === 'N/A' || !item.conditionUnitName ? '' : item.conditionUnitName;
  }
  function getTimelineDate(item: BiotwinHistoryItem) {
    const date = item.type === BiotwinHistoryType.Added ? item.conditionDate : item.date;
    return moment(date).startOf('day').toISOString();
  }
  function isBasic(item: BiotwinHistoryItem) {
    return ['Age,', 'Weight', 'Height', 'Male', 'Female'].includes(item.conditionName);
  }
  function createdAtFactory() {
    const basicItem = biotwinConditionsHistory.find((item) => isBasic(item) && item.type === BiotwinHistoryType.Added);
    return basicItem == null ? null : getTimelineDate(basicItem);
  }
  function tableValueFactory() {
    return biotwinConditionsHistory.slice().sort((a, b) => {
      return (new Date(a.date).getTime() - new Date(b.date).getTime()) * (order === 'asc' ? 1 : -1);
    });
  }
  function timelineValueFactory() {
    const itemsByConditionName = groupBy(
      biotwinConditionsHistory.filter(
        (item) =>
          !item.isPerformedBySystem &&
          (!globalFilter || item.conditionName.toLowerCase().includes(globalFilter.toLowerCase())),
      ),
      (item) => item.conditionName,
    );
    const filteredItems = Object.values(itemsByConditionName)
      .flatMap(timelineValueFactoryHelper)
      .sort((a, b) => {
        const aDate = a.type === BiotwinHistoryType.Added ? a.conditionDate : a.date;
        const bDate = b.type === BiotwinHistoryType.Added ? b.conditionDate : b.date;
        return (new Date(aDate).getTime() - new Date(bDate).getTime()) * (order === 'asc' ? 1 : -1);
      });
    return Object.entries(groupBy(filteredItems, getTimelineDate)) as [string, BiotwinHistoryItem[]][];
  }
  function timelineValueFactoryHelper(sameConditionItems: BiotwinHistoryItem[]) {
    const result: BiotwinHistoryItem[] = [];
    let willBeAddedItem: BiotwinHistoryItem | null = null;
    let lastAddedItem: BiotwinHistoryItem | null = null;
    let activeDate = '';
    let activeType: BiotwinHistoryType | null = null;
    const sortedSameConditionsItems = sameConditionItems
      .slice()
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const {conditionDate, conditionNote, conditionUnitName, conditionValue} =
      sortedSameConditionsItems[sortedSameConditionsItems.length - 1];
    sortedSameConditionsItems
      .concat([
        {
          date: moment('9999-01-01').toISOString(),
        } as BiotwinHistoryItem,
      ])
      .forEach((item, index, array) => {
        const prevItem = array[index - 1];
        const date = moment(item.date).startOf('day').toISOString();
        const isConditionDateChangeItem =
          item.type === BiotwinHistoryType.Changed && prevItem?.conditionDate !== item.conditionDate;
        if (willBeAddedItem == null) {
          if (!isConditionDateChangeItem) {
            willBeAddedItem = item;
            activeDate = date;
            activeType = item.type;
          }
        } else if (date !== activeDate) {
          result.push({
            ...willBeAddedItem,
            conditionDate,
            conditionNote,
            conditionUnitName,
            conditionValue,
          });
          lastAddedItem = willBeAddedItem;
          if (isConditionDateChangeItem) {
            willBeAddedItem = null;
            activeDate = '';
            activeType = null;
          } else {
            willBeAddedItem = item;
            activeDate = date;
            activeType = item.type;
          }
        } else {
          if (activeType === BiotwinHistoryType.Added && item.type === BiotwinHistoryType.Added) {
            // This is not possible.
          } else if (activeType === BiotwinHistoryType.Added && item.type === BiotwinHistoryType.Changed) {
            // Nothing to do here.
          } else if (activeType === BiotwinHistoryType.Added && item.type === BiotwinHistoryType.Removed) {
            activeType = null;
            willBeAddedItem = null;
          } else if (activeType === BiotwinHistoryType.Changed && item.type === BiotwinHistoryType.Added) {
            // This is not possible.
          } else if (activeType === BiotwinHistoryType.Changed && item.type === BiotwinHistoryType.Changed) {
            // Nothing to do here.
          } else if (activeType === BiotwinHistoryType.Changed && item.type === BiotwinHistoryType.Removed) {
            activeType = item.type;
            willBeAddedItem = item;
          } else if (activeType === BiotwinHistoryType.Removed && item.type === BiotwinHistoryType.Added) {
            activeType = null;
            willBeAddedItem = null;
          } else if (activeType === BiotwinHistoryType.Removed && item.type === BiotwinHistoryType.Changed) {
            // This is not possible.
          } else if (activeType === BiotwinHistoryType.Removed && item.type === BiotwinHistoryType.Removed) {
            // This is not possible.
          }
        }
      });
    return result;
  }
  function getTypeIcon(item: BiotwinHistoryItem): IconProp {
    switch (item.type) {
      case BiotwinHistoryType.Added:
        return 'plus';
      case BiotwinHistoryType.Removed:
        return 'minus';
      case BiotwinHistoryType.Changed:
        return 'pencil';
      default:
        return 'question';
    }
  }
  function getTypeText(item: BiotwinHistoryItem) {
    switch (item.type) {
      case BiotwinHistoryType.Added:
        return 'Added';
      case BiotwinHistoryType.Removed:
        return 'Removed';
      case BiotwinHistoryType.Changed:
        return 'Changed';
      default:
        return '';
    }
  }
  function getTypeColor(item: BiotwinHistoryItem) {
    switch (item.type) {
      case BiotwinHistoryType.Added:
        return 'green-600';
      case BiotwinHistoryType.Removed:
        return 'red-400';
      case BiotwinHistoryType.Changed:
        return 'yellow-600';
      default:
        return undefined;
    }
  }
};
